import { c as _c } from "react/compiler-runtime";
import { BadgeCheck, FileText } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { businessFormLabel, surveyLabel, surveyList } from "@/lib/utils";
import { formatDateish } from "@osmix/formatters";
import { useQueryClient } from "@tanstack/react-query";
import { api, type ProfileDocument, type SellerShopStatus } from "@/api";
import { useApplicationBoard } from "@/api/hooks/use-applications";
import { sellerKeys, useSeller, useSellerSections, useSellerVerification } from "@/api/hooks/use-sellers";
import { ReworkDialog } from "@/components/application-rework";
import { useUsers } from "@/api/hooks/use-users";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DocumentViewer } from "@/components/document-viewer";
import { SellerDocumentsPanel } from "@/components/seller-documents";
import { allSectionsAccepted, SellerSectionsPanel } from "@/components/seller-sections";
import { SellerVerificationPanel } from "@/components/seller-verification";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Select } from "@/components/ui/select";
import { Separator } from "@osmix/ui/separator";

// Shop verification, rendered on the STOREFRONT detail page. The moderation
// record is keyed by the seller's USER id (api.sellers / gateway sellerShops),
// so the storefront page resolves its owning seller and passes that id here.
// Shows the shop submission (profile, requisites, documents, founders) and the
// approve/reject/block actions. The seller's own survey lives on the user page
// (see components/seller-survey.tsx).

type ActionKind = "approve" | "rework" | "reject" | "block" | "unblock";
const STATUS_VARIANT: Record<SellerShopStatus, "default" | "secondary" | "destructive" | "outline"> = {
  filling: "outline",
  on_moderation: "default",
  active: "secondary",
  blocked: "destructive"
};
const ACTIONS_FOR: Record<SellerShopStatus, ActionKind[]> = {
  filling: [],
  // «На доработку» — замечание со сроком (заявка «На доработке»);
  // «Отказать» — окончательно, повторная подача через 90 дней (26.09.2026).
  on_moderation: ["approve", "rework", "reject"],
  active: ["block"],
  blocked: ["unblock"]
};
const ACTION_VARIANT: Record<ActionKind, "default" | "outline" | "destructive"> = {
  approve: "default",
  rework: "outline",
  reject: "outline",
  block: "destructive",
  unblock: "outline"
};
export function ShopModeration({
  userId
}: {
  userId: string;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const shopQuery = useSeller(userId);
  const shop = shopQuery.data ?? null;
  const usersQuery = useUsers();
  // Заявка воронки для этой секции ре-модерации. Онбординговая заявка продавца
  // имеет детерминированный id `app_<userId>` (api-gateway/applications), что и
  // связывает карточку продавца с эндпоинтом доработки, — доска не отдаёт
  // userId. Заявку с лендинга (случайный id) так не найти: тогда посекционной
  // доработки в этой карточке нет, и это ожидаемо.
  const boardQuery = useApplicationBoard();
  const application = boardQuery.data?.applications.find(entry => entry.id === `app_${userId}`) ?? null;
  const managerCandidates = (usersQuery.data ?? []).filter(u => u.role === "admin");
  // Кандидаты в тимлиды — сотрудники платформы (у кого есть platform-роль),
  // в отличие от менеджера-куратора, которым может быть только админ.
  const teamLeadCandidates = (usersQuery.data ?? []).filter(u_0 => u_0.platformRole);
  const [actionError, setActionError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [pending, setPending] = useState<ActionKind | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [viewing, setViewing] = useState<ProfileDocument | null>(null);
  const [reworkOpen, setReworkOpen] = useState(false);
  const verificationQuery = useSellerVerification(userId);
  // П7 (fd-onboard-27): одобрить можно только при всех принятых разделах.
  const sectionsQuery = useSellerSections(userId);
  const sectionsReady = allSectionsAccepted(sectionsQuery.data);
  const allowedDocTypes = verificationQuery.data?.checklist.map(item => item.docType);
  const [managerId, setManagerId] = useState("");
  const [managerSaving, setManagerSaving] = useState(false);
  const [leadId, setLeadId] = useState("");
  const [leadSaving, setLeadSaving] = useState(false);
  // Тимлид хранится по shopId витрины (или userId продавца без витрины) —
  // именно этот ключ ждёт эндпоинт /admin/shops/:id/team-lead.
  const teamLeadKey = shop ? shop.shopId ?? shop.id : "";
  const loadError = shopQuery.error ?? usersQuery.error;
  const error = actionError ?? (loadError ? loadError.message : null);

  // Seed the manager select once per seller id, from the fetched record —
  // later background refetches must not wipe an in-progress selection.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!shop || seededFor.current === userId) return;
    seededFor.current = userId;
    setManagerId(shop.manager?.id ?? "");
    setLeadId(shop.teamLead?.userId ?? "");
  }, [shop, userId]);
  const confirmAction = async () => {
    if (!pending) return;
    if (pending === "reject" && !rejectionReason.trim()) {
      setActionError(t("sellers.rejectReasonRequired"));
      return;
    }
    setActing(true);
    try {
      if (pending === "reject") await api.sellers.reject(userId, rejectionReason.trim(), {
        final: true
      });else if (pending === "approve" || pending === "block" || pending === "unblock") await api.sellers[pending](userId);
      await queryClient.invalidateQueries({
        queryKey: sellerKeys.all
      });
      setPending(null);
      setRejectionReason("");
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setActing(false);
    }
  };
  const saveManager = async () => {
    if (!shop) return;
    setManagerSaving(true);
    try {
      const next = managerId ? await api.sellers.assignManager(userId, managerId) : await api.sellers.clearManager(userId);
      queryClient.setQueryData(sellerKeys.detail(userId), next);
      setManagerId(next.manager?.id ?? "");
    } catch (e_0) {
      setActionError((e_0 as Error).message);
    } finally {
      setManagerSaving(false);
    }
  };
  const removeManager = async () => {
    if (!shop?.manager) return;
    setManagerSaving(true);
    try {
      const next_0 = await api.sellers.clearManager(userId);
      queryClient.setQueryData(sellerKeys.detail(userId), next_0);
      setManagerId("");
    } catch (e_1) {
      setActionError((e_1 as Error).message);
    } finally {
      setManagerSaving(false);
    }
  };

  // Эндпоинт тимлида возвращает саму привязку, а не деталь магазина, поэтому
  // после назначения/снятия перечитываем деталь — так teamLead в карточке
  // обновится актуальными name/email с бэкенда.
  const saveTeamLead = async () => {
    if (!shop) return;
    setLeadSaving(true);
    try {
      if (leadId) await api.sellers.assignTeamLead(teamLeadKey, leadId);else await api.sellers.clearTeamLead(teamLeadKey);
      await queryClient.invalidateQueries({
        queryKey: sellerKeys.detail(userId)
      });
    } catch (e_2) {
      setActionError((e_2 as Error).message);
    } finally {
      setLeadSaving(false);
    }
  };
  const removeTeamLead = async () => {
    if (!shop?.teamLead) return;
    setLeadSaving(true);
    try {
      await api.sellers.clearTeamLead(teamLeadKey);
      await queryClient.invalidateQueries({
        queryKey: sellerKeys.detail(userId)
      });
      setLeadId("");
    } catch (e_3) {
      setActionError((e_3 as Error).message);
    } finally {
      setLeadSaving(false);
    }
  };
  const dash = (v?: string) => v && v.trim() ? v : "—";
  const list = (v_0?: string[]) => v_0 && v_0.length ? v_0.join(", ") : "—";
  if (!shop && !error) return null;
  return <section className="space-y-4">
			<div className="flex flex-wrap items-center gap-3">
				<h2 className="text-lg font-semibold">
					{t("storefronts.detail.verification")}
				</h2>
				{shop && <Badge variant={STATUS_VARIANT[shop.status]}>
						{t(`sellers.status.${shop.status}`)}
					</Badge>}
				{shop?.verified && <Badge variant="secondary" className="gap-1">
						<BadgeCheck className="size-3.5" />
						{t("sellers.verifiedBadge")}
					</Badge>}
				<div className="flex-1" />
				{shop && ACTIONS_FOR[shop.status]
      // Доработка идёт через заявку воронки: без неё замечание некуда записать.
      .filter(kind => kind !== "rework" || application).map(kind_0 => <Button key={kind_0} size="sm" variant={ACTION_VARIANT[kind_0]} disabled={acting || kind_0 === "approve" && !sectionsReady} title={kind_0 === "approve" && !sectionsReady ? t("p7.approveNeedsSections", {
        defaultValue: "Одобрить можно, когда приняты все разделы анкеты"
      }) : undefined} data-testid={`shop-action-${kind_0}`} onClick={() => kind_0 === "rework" ? setReworkOpen(true) : setPending(kind_0)}>
							{t(`sellers.actions.${kind_0}`)}
						</Button>)}
			</div>

			{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{error}
				</div>}

			{/* Заявка на доработке: продавец исправляет замечания, магазин всё ещё
       «на модерации» — без плашки карточка выглядела нетронутой. */}
			{application?.status === "rework" && <div className="rounded-md border border-orange-500/40 bg-orange-500/10 p-3 text-sm text-orange-800 dark:text-orange-300">
					{t("sellers.reworkBanner", {
        date: formatDateish(application.statusChangedAt)
      })}
				</div>}
			{application?.status === "rejected" && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{t("sellers.rejectedBanner", {
        date: formatDateish(application.statusChangedAt)
      })}
				</div>}

			{shop && <>
					{shop.rejectionReason && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
							<div className="font-medium">{t("sellers.rejectReasonTitle")}</div>
							<div className="mt-1 whitespace-pre-line">{shop.rejectionReason}</div>
						</div>}
					<Card>
						<CardHeader>
							<CardTitle>{t("sellers.manager.title")}</CardTitle>
							<p className="text-sm text-muted-foreground">
								{t("sellers.manager.subtitle")}
							</p>
						</CardHeader>
						<CardContent className="space-y-4 text-sm">
							{shop.manager ? <div className="grid gap-2 md:grid-cols-3">
									<Row label={t("sellers.manager.name")}>
										{dash(shop.manager.name)}
									</Row>
									<Row label={t("sellers.manager.email")}>
										{dash(shop.manager.email)}
									</Row>
									<Row label={t("sellers.manager.phone")}>
										{dash(shop.manager.phone)}
									</Row>
								</div> : <p className="text-muted-foreground">
									{t("sellers.manager.empty")}
								</p>}
							<div className="flex flex-wrap items-center gap-3">
								<Select value={managerId} onChange={e_4 => setManagerId(e_4.target.value)} className="max-w-xs">
									<option value="">{t("sellers.manager.unassigned")}</option>
									{managerCandidates.map(manager => <option key={manager.id} value={manager.id}>
											{manager.name} · {manager.email}
										</option>)}
								</Select>
								<Button size="sm" disabled={managerSaving || managerId === (shop.manager?.id ?? "")} onClick={saveManager}>
									{managerSaving ? t("sellers.manager.saving") : t("sellers.manager.save")}
								</Button>
								{shop.manager ? <Button size="sm" variant="outline" disabled={managerSaving} onClick={removeManager}>
										{managerSaving ? t("sellers.manager.removing") : t("sellers.manager.remove")}
									</Button> : null}
							</div>
						</CardContent>
					</Card>

					{/* Тимлид магазина — отдельная роль: курирует модерацию товаров
         этого магазина. Подписан явно, чтобы не путать с менеджером-
         куратором продавца выше. */}
					<Card>
						<CardHeader>
							<CardTitle>{t("sellers.teamLead.title")}</CardTitle>
							<p className="text-sm text-muted-foreground">
								{t("sellers.teamLead.subtitle")}
							</p>
						</CardHeader>
						<CardContent className="space-y-4 text-sm">
							{shop.teamLead ? <div className="grid gap-2 md:grid-cols-3">
									<Row label={t("sellers.teamLead.name")}>
										{dash(shop.teamLead.name)}
									</Row>
									<Row label={t("sellers.teamLead.email")}>
										{dash(shop.teamLead.email)}
									</Row>
									<Row label={t("sellers.teamLead.assignedAt")}>
										{shop.teamLead.assignedAt ? new Date(shop.teamLead.assignedAt).toLocaleDateString() : "—"}
									</Row>
								</div> : <p className="text-muted-foreground">
									{t("sellers.teamLead.empty")}
								</p>}
							<div className="flex flex-wrap items-center gap-3">
								<Select value={leadId} onChange={e_5 => setLeadId(e_5.target.value)} className="max-w-xs">
									<option value="">{t("sellers.teamLead.unassigned")}</option>
									{teamLeadCandidates.map(lead => <option key={lead.id} value={lead.id}>
											{lead.name}
											{lead.platformRole ? ` · ${lead.platformRole.name}` : ""}
										</option>)}
								</Select>
								<Button size="sm" disabled={leadSaving || leadId === (shop.teamLead?.userId ?? "")} onClick={saveTeamLead}>
									{leadSaving ? t("sellers.teamLead.saving") : t("sellers.teamLead.save")}
								</Button>
								{shop.teamLead ? <Button size="sm" variant="outline" disabled={leadSaving} onClick={removeTeamLead}>
										{leadSaving ? t("sellers.teamLead.removing") : t("sellers.teamLead.remove")}
									</Button> : null}
							</div>
						</CardContent>
					</Card>

					{/* П7: разделы анкеты - решение по каждому, пометка «изменён после проверки». */}
					<SellerSectionsPanel userId={userId} canModerate={shop.status === "on_moderation" || shop.status === "active"} />

					{/* Э7: сводка верификации — чек-лист документов (3 состояния),
         авто-проверка ИНН и бейдж «Проверенный продавец». */}
					<SellerVerificationPanel userId={userId} application={application} />

					{/* Э7: «личное дело» — документы верификации и их модерация. */}
					<SellerDocumentsPanel userId={userId} />

					<div className="grid gap-6 lg:grid-cols-2">
						<Section title={t("sellers.detail.shopProfile")}>
						<Row label={t("sellers.fields.shopName")}>
							{dash(shop.shop.shopName)}
						</Row>
						<Row label={t("sellers.fields.founded")}>
							{dash(shop.shop.foundingDate)}
						</Row>
						<Row label={t("sellers.fields.mainProduct")}>
							{dash(shop.shop.mainProduct)}
						</Row>
						<Row label={t("sellers.fields.role")}>{dash(surveyLabel("role", shop.shop.role))}</Row>
						<Row label={t("sellers.fields.categories")}>
							{surveyList("category", shop.shop.categories)}
						</Row>
						<Row label={t("sellers.fields.salesChannels")}>
							{surveyList("channel", shop.shop.salesChannels)}
						</Row>
						<Separator />
						<div className="space-y-1">
							<span className="text-muted-foreground">
								{t("sellers.fields.description")}
							</span>
							<p className="whitespace-pre-line">
								{dash(shop.shop.brandDescription)}
							</p>
						</div>
						{shop.shop.socials && Object.values(shop.shop.socials).some(Boolean) && <>
									<Separator />
									{Object.entries(shop.shop.socials).filter(([, v_1]) => v_1).map(([k, v_2]) => <Row key={k} label={k}>
												{v_2}
											</Row>)}
								</>}
					</Section>

					<Section title={t("sellers.detail.requisites")}>
						<Row label={t("sellers.fields.organizationForm")}>
							{dash(businessFormLabel(t, shop.requisites.organizationForm))}
						</Row>
						<Row label={t("sellers.fields.fullName")}>
							{dash(shop.requisites.fullName)}
						</Row>
						<Row label={t("sellers.fields.inn")}>
							{dash(shop.requisites.inn)}
						</Row>
						<Row label={t("sellers.fields.ogrn")}>
							{dash(shop.requisites.ogrn)}
						</Row>
						<Row label={t("sellers.fields.kpp")}>
							{dash(shop.requisites.kpp)}
						</Row>
						<Row label={t("sellers.fields.legalAddress")}>
							{dash(shop.requisites.legalAddress)}
						</Row>
						<Separator />
						<Row label={t("sellers.fields.bankAccount")}>
							{dash(shop.requisites.bankAccount)}
						</Row>
						<Row label={t("sellers.fields.bik")}>
							{dash(shop.requisites.bik)}
						</Row>
						<Row label={t("sellers.fields.accountant")}>
							{dash(shop.requisites.accountantContact)}
						</Row>
					</Section>

					<Section title={t("sellers.detail.documents")}>
						{shop.documents.length === 0 && <p className="text-muted-foreground">
								{t("sellers.detail.noDocuments")}
							</p>}
						{shop.documents.map(d => {
            // Only an uploaded/approved doc has bytes in the bucket; a
            // pending/rejected slot is just a label, not a link.
            const viewable = d.status === "uploaded" || d.status === "approved";
            return <div key={d.id} className="flex items-center justify-between gap-3">
									{viewable ? <button type="button" onClick={() => setViewing(d)} className="inline-flex items-center gap-2 text-left text-primary underline-offset-2 hover:underline">
											<FileText className="size-4" />
											<span>{d.fileName ?? d.name}</span>
										</button> : <span className="inline-flex items-center gap-2 text-muted-foreground">
											<FileText className="size-4" />
											<span>{d.name}</span>
										</span>}
									<Badge variant={viewable ? "secondary" : "outline"}>
										{t(`sellers.docStatus.${d.status}`)}
									</Badge>
								</div>;
          })}
						<Separator />
						<div className="space-y-3">
							<span className="text-sm font-medium">
								{t("sellers.detail.founders")}
							</span>
							{shop.founders.length === 0 && <p className="text-muted-foreground">
									{t("sellers.detail.noFounders")}
								</p>}
							{shop.founders.map(f => <div key={f.id} className="text-sm">
									<div className="font-medium">{dash(f.fullName)}</div>
									<div className="text-muted-foreground">
										{dash(f.position)} · {dash(f.phone)} · {dash(f.email)}
									</div>
								</div>)}
						</div>
					</Section>
					</div>
				</>}

			<ConfirmDialog open={!!pending} busy={acting} title={pending && shop ? `${t(`sellers_dialog.${pending}Title`)} — ${shop.shopName}` : ""} description={pending ? t(`sellers_dialog.${pending}Body`) : undefined} confirmLabel={pending ? t(`sellers.actions.${pending}`) : undefined} confirmVariant={pending === "reject" || pending === "block" ? "destructive" : "default"} onConfirm={confirmAction} onClose={() => {
      setPending(null);
      setRejectionReason("");
    }}>
				{pending === "reject" && <textarea value={rejectionReason} onChange={e_6 => setRejectionReason(e_6.target.value)} placeholder={t("sellers.rejectReasonPlaceholder")} className="min-h-24 w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-ring" />}
			</ConfirmDialog>

			{application && <ReworkDialog open={reworkOpen} applicationId={application.id} shopName={shop?.shopName} allowedDocTypes={allowedDocTypes} onClose={() => setReworkOpen(false)} onDone={() => void queryClient.invalidateQueries({
      queryKey: sellerKeys.all
    })} />}

			<DocumentViewer userId={userId} doc={viewing} onClose={() => setViewing(null)} />
		</section>;
}
function Section(t0) {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <CardHeader><CardTitle>{title}</CardTitle></CardHeader>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <CardContent className="space-y-3 text-sm">{children}</CardContent>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Card>{t1}{t2}</Card>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function Row(t0) {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-muted-foreground">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span className="text-right">{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex justify-between gap-4">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}